/**
 * High-performance connection progress store
 */

import { create } from "zustand";
import type {
  ConnectionProgressBatch,
  ConnectionProgressState,
  ConnectionProgressStore,
  ConnectionProgressMessage,
} from "../types";

const initialState: ConnectionProgressState = {
  byId: new Map(),
  messageHistory: [],
  lastMessage: null,
  isConnected: false,
  hasIssues: false,
  isConnecting: false,
  connectionStartTime: null,
  connectionEndTime: null,
};

export const useConnectionProgressStore = create<ConnectionProgressStore>(
  (set, get) => {
    // Create stable function references
    const getMessageById = (id: string) => get().byId.get(id);
    const getConnectionStatus = () => {
      const state = get();
      return {
        isConnected: state.isConnected,
        hasIssues: state.hasIssues,
        isConnecting: state.isConnecting,
      };
    };
    return {
      ...initialState,

      processMessage: (data: ConnectionProgressBatch) => {
        set((state) => {
          const now = new Date();
          const messageId = `${data.message}-${data.progress}-${
            data.type
          }-${now.getTime()}`;

          // Create new message
          const newMessage: ConnectionProgressMessage = {
            id: messageId,
            message: data.message,
            progress: data.progress,
            type: data.type,
            timestamp: now,
          };

          // Clone maps for immutability
          const newById = new Map(state.byId);
          newById.set(messageId, newMessage);

          // Add to history (newest first)
          const newHistory = [messageId, ...state.messageHistory];

          // Determine connection status
          // Type meanings: 0 = in-progress, 1 = warning, 2 = error/failure, 3 = success
          const loginCompleted = data.type === 3 && data.progress === 100;
          // LATCHED: once the login handshake completed, an info/warning frame that arrives later (the
          // gateway narrates resubscribes, history reloads, engine notices) must not flip the account
          // back to "disconnected" — that dashed Balance/Equity/Leverage/Ccy on the mobile Settings card
          // and painted the dot red on a perfectly live socket (SR Global, 2026-09-08). Only an error
          // frame, or a real socket-state change via setSocketState(), ends a connected session.
          const isConnected =
            loginCompleted || (state.isConnected && data.type !== 2);
          const hasIssues =
            data.type === 2 || (state.hasIssues && !isConnected); // Once error, stay in error state until connected
          const isConnecting =
            !isConnected && !hasIssues && newHistory.length > 0;

          // Keep first error message - don't overwrite with subsequent errors
          // This ensures we show "Exception: INVALID_ACCOUNT" instead of generic "Connection failed"
          const isFirstError = data.type === 2 && !state.hasIssues;
          const shouldUpdateLastMessage =
            data.type !== 2 || isFirstError || isConnected; // Update on success to clear error

          // Set connection timing
          const connectionStartTime =
            state.connectionStartTime ||
            (newHistory.length === 1 ? now : state.connectionStartTime);
          const connectionEndTime = loginCompleted
            ? now
            : isConnected
              ? state.connectionEndTime
              : null;

          // Log error states for debugging
          if (data.type === 2) {
            if (isFirstError) {
            } else {
            }
          } else if (isConnected) {
          }

          const newState = {
            ...state,
            byId: newById,
            messageHistory: newHistory,
            lastMessage: shouldUpdateLastMessage
              ? newMessage
              : state.lastMessage, // Keep first error message
            isConnected,
            hasIssues,
            isConnecting,
            connectionStartTime,
            connectionEndTime,
          };

          return newState;
        });
      },
      setSocketState: (socketState) => {
        set((state) => {
          switch (socketState) {
            case "connected":
              return {
                ...state,
                isConnected: true,
                isConnecting: false,
                hasIssues: false,
                connectionEndTime: new Date(),
              };
            case "connecting":
              // A latched error (e.g. rejected login) is not cleared by mere
              // reconnect attempts — keep the dot red until a real success.
              return {
                ...state,
                isConnected: false,
                isConnecting: !state.hasIssues,
              };
            case "error":
              return {
                ...state,
                isConnected: false,
                isConnecting: false,
                hasIssues: true,
              };
            case "disconnected":
            default:
              return {
                ...state,
                isConnected: false,
                isConnecting: false,
              };
          }
        });
      },

      reset: () => {
        set({
          ...initialState,
          byId: new Map(),
          messageHistory: [],
        });
      },

      // Stable function references
      getMessageById,
      getConnectionStatus,
    };
  }
);
